import { renderInlineLoader } from "/assets/themes/senses/js/async-loader.min.js"

const NATIVE_SUBMIT_GRACE_MS = 5000
const buttonStates = new Map()
const formStates = new Map()
let installed = false

// Keep controls natively enabled so submitter values remain serializable and
// keyboard focus is not discarded. Pointer blocking plus the capture-phase
// re-entrancy guard prevents duplicate activation while aria-disabled exposes
// the temporary state.
export function setButtonBusy(button) {
    if (
        !(button instanceof HTMLButtonElement)
        || buttonStates.has(button)
        || button.hasAttribute('data-edit-mode-command')
    ) {
        return
    }

    const width = button.getBoundingClientRect().width
    const state = {
        width: button.style.width,
        minWidth: button.style.minWidth,
        pointerEvents: button.style.pointerEvents,
        hadAriaBusy: button.hasAttribute('aria-busy'),
        ariaBusy: button.getAttribute('aria-busy'),
        hadAriaDisabled: button.hasAttribute('aria-disabled'),
        ariaDisabled: button.getAttribute('aria-disabled'),
    }
    buttonStates.set(button, state)

    // Reserve the original width so the button cannot SHRINK, but let it grow:
    // freezing the width clipped the retained label once the spinner and its gap
    // were inserted, and a text-only button could overlap its neighbour.
    if (width > 0) {
        button.style.minWidth = `${Math.ceil(width)}px`
    }
    button.style.pointerEvents = 'none'
    button.setAttribute('aria-busy', 'true')
    button.setAttribute('aria-disabled', 'true')

    // Keep the original content in the accessibility tree. In particular, an
    // icon-only control may derive its name from that content; replacing it
    // with a spinner made the busy button anonymous. aria-busy/aria-disabled
    // carry the state while the spinner is decorative.
    button.insertAdjacentHTML(
        'afterbegin',
        `<span data-radaptor-request-loading-spinner>${renderInlineLoader({
            label: '',
            size: 16,
            announce: false,
        })}</span>`,
    )
}

export function clearButtonBusy(button) {
    const state = buttonStates.get(button)
    if (!state) {
        return
    }

    button.querySelector('[data-radaptor-request-loading-spinner]')?.remove()
    button.style.width = state.width
    button.style.minWidth = state.minWidth
    button.style.pointerEvents = state.pointerEvents
    if (state.hadAriaBusy) {
        button.setAttribute('aria-busy', state.ariaBusy)
    } else {
        button.removeAttribute('aria-busy')
    }
    if (state.hadAriaDisabled) {
        button.setAttribute('aria-disabled', state.ariaDisabled)
    } else {
        button.removeAttribute('aria-disabled')
    }
    buttonStates.delete(button)
}

function beginFormBusy(form, submitter) {
    const state = {
        submitter: submitter instanceof HTMLButtonElement ? submitter : null,
        submitted: false,
        retained: false,
    }
    formStates.set(form, state)
    setButtonBusy(state.submitter)

    return state
}

export function retainFormBusy(form) {
    const state = formStates.get(form)
    if (state) {
        state.retained = true
    }
}

export function clearFormBusy(form) {
    const state = formStates.get(form)
    if (!state) {
        return
    }

    if (state.watchdog) {
        window.clearTimeout(state.watchdog)
    }
    clearButtonBusy(state.submitter)
    formStates.delete(form)
}

function formForHtmxEvent(event) {
    const source = event.detail?.elt || event.target
    if (source instanceof HTMLFormElement) {
        return source
    }
    if (source instanceof Element) {
        return source.closest('form')
    }

    return null
}

function buttonForHtmxEvent(event) {
    const source = event.detail?.elt || event.target
    if (source instanceof HTMLButtonElement) {
        return source
    }
    if (source instanceof Element) {
        return source.closest('button')
    }

    return null
}

function clearAllBusy() {
    Array.from(formStates.keys()).forEach(clearFormBusy)
    Array.from(buttonStates.keys()).forEach(clearButtonBusy)
}

// Runs once the event that is being dispatched has reached every listener: a macrotask, unlike a
// microtask, cannot run between two listeners of one dispatch, so it sees the final
// `defaultPrevented`.
function afterDispatch(callback) {
    window.setTimeout(callback, 0)
}

export function installSubmitButtonBusy() {
    if (installed) {
        return
    }
    installed = true

    document.addEventListener('click', (event) => {
        const submitter = event.target instanceof Element ? event.target.closest('button') : null
        if (submitter instanceof HTMLButtonElement && buttonStates.has(submitter)) {
            event.preventDefault()
            event.stopImmediatePropagation()
            return
        }
        const form = submitter instanceof HTMLButtonElement && submitter.type === 'submit'
            ? submitter.form
            : null
        if (!form) {
            return
        }

        if (formStates.has(form)) {
            event.preventDefault()
            event.stopImmediatePropagation()
            return
        }

        const state = beginFormBusy(form, submitter)
        // Decide after the WHOLE dispatch: a listener later in the chain (a bubble-phase confirm()
        // guard, an inline handler) may still cancel the click. A microtask runs between listeners,
        // before those cancel, and left the button spinning after a dismissed confirm.
        afterDispatch(() => {
            if (formStates.get(form) === state && !state.submitted && event.defaultPrevented) {
                clearFormBusy(form)
            }
        })
    }, true)

    document.addEventListener('submit', (event) => {
        if (!(event.target instanceof HTMLFormElement)) {
            return
        }

        const form = event.target
        const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null
        const state = formStates.get(form)
        let activeState = state
        if (state) {
            if (state.submitted || (submitter && state.submitter !== submitter)) {
                event.preventDefault()
                event.stopImmediatePropagation()
                return
            }
            state.submitted = true
        } else {
            activeState = beginFormBusy(form, submitter)
            activeState.submitted = true
        }

        // After the whole dispatch, for the same reason as the click guard above: a submit listener
        // registered after this capture-phase one (e.g. `if (!confirm(...)) event.preventDefault()`)
        // must still release the button when it cancels.
        afterDispatch(() => {
            const currentState = formStates.get(form)
            if (currentState !== activeState) {
                return
            }

            if (event.defaultPrevented) {
                if (!activeState.retained) {
                    clearFormBusy(form)
                }

                return
            }

            // A native submission normally destroys this document, so nothing has
            // to clear the busy state. It does NOT when the response is an
            // attachment (the export download): the page stays put and the button
            // would spin forever, blocking further exports until a reload.
            //
            // Only GET submissions get that self-heal timer. GET is declared safe
            // and idempotent — re-enabling one can at worst repeat a harmless
            // request — and it is the shape that serves attachments. A POST is
            // never re-armed by a timer: a slow navigation is still in flight, and
            // re-enabling its button would let a second click duplicate
            // server-side work (invite creation, passkey removal).
            if (form.method.toLowerCase() !== 'get') {
                return
            }

            activeState.watchdog = window.setTimeout(() => {
                if (formStates.get(form) === activeState) {
                    clearFormBusy(form)
                }
            }, NATIVE_SUBMIT_GRACE_MS)
        })
    }, true)

    document.addEventListener('invalid', (event) => {
        if (event.target instanceof HTMLElement && event.target.form) {
            clearFormBusy(event.target.form)
        }
    }, true)

    document.addEventListener('htmx:beforeRequest', (event) => {
        const form = formForHtmxEvent(event)
        const button = buttonForHtmxEvent(event)
        if (!form && !button) {
            return
        }

        if (form) {
            retainFormBusy(form)
        }
        setButtonBusy(button)

        const xhr = event.detail?.xhr
        if (xhr && typeof xhr.addEventListener === 'function') {
            // Capture the originating nodes now. HTMX may replace or detach
            // them before its document events run; deriving them again from an
            // afterSwap/afterRequest event then clears the wrong node or none.
            xhr.addEventListener('loadend', () => {
                if (isNavigationXhr(xhr)) {
                    return
                }
                if (form) {
                    clearFormBusy(form)
                }
                clearButtonBusy(button)
            }, { once: true })
        }

        queueMicrotask(() => {
            if (event.defaultPrevented) {
                if (form) {
                    clearFormBusy(form)
                }
                clearButtonBusy(button)
            }
        })
    }, true)

    // A response that tears this document down (HX-Redirect assigns
    // window.location, HX-Refresh reloads) still COMPLETES as a request first,
    // so the cleanup events below fire while the browser is only about to
    // leave. Clearing then re-enables the button for the whole redirect flight
    // - long enough for a duplicate second click. Hold the busy state instead:
    // the new document replaces this one, and a bfcache restore is already
    // reset by the pageshow handler below. HX-Location is deliberately NOT
    // held: htmx serves it with an internal follow-up request that can swap a
    // fragment while this form stays on the page, so suppressing its cleanup
    // would wedge the button busy forever.
    function isNavigationXhr(xhr) {
        if (!xhr || xhr.readyState !== XMLHttpRequest.DONE) {
            return false
        }
        try {
            return Boolean(
                xhr.getResponseHeader('HX-Redirect')
                || xhr.getResponseHeader('HX-Refresh') === 'true',
            )
        } catch {
            return false
        }
    }

    // Fallback for synthetic HTMX integrations that do not expose an XHR on
    // beforeRequest. Real requests use the captured loadend listener above.
    ;['htmx:afterRequest', 'htmx:responseError']
        .forEach((eventName) => {
            document.addEventListener(eventName, (event) => {
                if (isNavigationXhr(event.detail?.xhr)) {
                    return
                }

                const form = formForHtmxEvent(event)
                if (form) {
                    clearFormBusy(form)
                }
                clearButtonBusy(buttonForHtmxEvent(event))
            })
        })

    // Transport failures never navigate - always release the button.
    ;['htmx:sendError', 'htmx:timeout']
        .forEach((eventName) => {
            document.addEventListener(eventName, (event) => {
                const form = formForHtmxEvent(event)
                if (form) {
                    clearFormBusy(form)
                }
                clearButtonBusy(buttonForHtmxEvent(event))
            })
        })

    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            clearAllBusy()
            window.dispatchEvent(new Event('radaptor:submit-busy-reset'))
        }
    })
}

// Install as soon as the layout's module is evaluated. The root Stimulus
// controller repeats this idempotently as a readable DOM contract, but the
// correctness guard must not wait for a dynamic controller import.
installSubmitButtonBusy()
